<template>
	<div class="tabs" @click.stop.prevent="handleClick">
		<nav class="tabs__nav">
			<button v-for="(tab, index) in tabs" v-bind:key="tab.title" :id="'tab-button-'+index" role="button" class="tabs__nav-item txt--strong" :class="{ 'tabs__nav-item--active bg--primary': tab.display }" v-on:click="selectTab(tab)">
				<span v-html="tab.title"></span>
				<i v-if="!tab.display" aria-hidden="true" class="fa fa-chevron-down ml-1 ml--1 show--md"></i>
				<i v-if="tab.display" aria-hidden="true" class="fa fa-chevron-up ml-1 ml--1 show--md"></i>	
			</button>
		</nav>
		<div class="tabs__content" id="tabcontent">
			<div v-for="(tab, index) in tabs" v-bind:key="tab.content">
				<div v-if="tab.display" :id="'tab-content-'+index" class="bdr--light-gray p--1 wysiwyg" role="tabpanel" :aria-labelledby="tab.title" v-html="tab.content"></div>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: "Tabs",
		methods: {					
			handleClick(e) {
				if (e.target.matches("a")) {
					let url = e.target.href;
					let target = e.target.target == "_blank" ? "_blank" : "_self";
					let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
					var isnativeapp = document.getElementById("isnativeapp");
					if (isnativeapp === undefined || isnativeapp === null) 
					{
						window.open(url,target);  
					}
					else{
						if(target == "_blank") {
							let data = {title:'',nodeid: dataId, href:url, target: target};
							window.Native('dataCallback', data);
						} 
					}	
				}
			},
			selectTab(selectedTab) {
				this.tabs.forEach(tab => {
					if(tab.display) {
						tab.display = false;
					} else {
						tab.display = (tab.title == selectedTab.title);
					}
				});
			}
		},
		data() {
			return {
				tabs: [
					{
						title: "When to use",
						display: false,
						accordion: false,
						content: `
							<ul>
								<li>Use this tool for adults at risk of malnutrition.</li>
								<li>MUST is for use in hospitals, community and other care settings and can be used by all care workers.</li>
								<li>
									<a href="https://www.bapen.org.uk/screening-and-must/30-screening-for-malnutrition/must" target="_blank">BAPEN</a> recommend that MUST should be used routinely for patients admitted to hospitals and care homes. 
									It should also be used with new patients attending general practitioner (GP) surgeries, 
									in those aged 75 years and over undertaking routine annual health assessments, in vulnerable groups, 
									and in those for whom there is clinical concern (e.g. those who are frail and elderly, 
									the poor and socially isolated, and those with severe diseases and disabilities). 
									Screening should be repeated at intervals depending on the healthcare setting and clinical condition. 
									The same tool should be used to screen patients at risk of malnutrition as they move from one healthcare 
									setting to another. <br><br>
									<a href="https://www.nice.org.uk/guidance/cg32/chapter/1-Guidance#screening-for-malnutrition-and-the-risk-of-malnutrition-in-hospital-and-the-community" target="_blank">NICE Clinical Guidance 32</a> requires that all patients are screened for malnutrition on admission to hospital and all outpatients at their first clinic appointment.
									Screening should be repeated weekly for inpatients and where there is clinical concern for outpatients.
								</li>
							</ul>
						`,
					},
					{
						title: "Evidence",
						display: false,
						accordion: false,
						content: `
							<p>
								The MUST was originally derived as a report based on expert consensus (<a href="https://www.bapen.org.uk/pdfs/must/must-report.pdf" target="_blank">Elia 2003</a>) and has since been externally validated in a number of studies.
							</p>
							<p>
								In a cohort study of 132 patients admitted under a general medical service, (<a href="https://pubmed.ncbi.nlm.nih.gov/28917223/" target="_blank">Sharma et al 2017</a>) found that, when compared to the Patient-Generated Subjective Global Assessment (PG-SGA), MUST was 69.7% sensitive and 75.8% specific for detecting malnutrition. Kappa statistics showed 72.7% agreement (k = 0.49). The study also showed that the MUST had a significant inverse correlation with BMI.
							</p>
							<p>
								<a href="https://pubmed.ncbi.nlm.nih.gov/15533269/" target="_blank">Stratton et al (2004)</a> found variable agreement with other similar nutritional assessment tools, ranging from poor (k = 0.255 for Undernutrition Risk Score) to excellent (k = 0.775-0.893 for <a href="https://www.mdcalc.com/nutrition-risk-screening-2002-nrs-2002" target="_blank">Nutrition Risk Screening 2002</a> and others). Notably, the study also suggested a high prevalence of malnutrition in both hospital inpatient and outpatient groups (19-60%)
							</p>
							<p>
								<a href="https://pubmed.ncbi.nlm.nih.gov/22142968/" target="_blank">Boléo-Tomé et al (2012)</a> validated MUST in a radiation oncology patient cohort and found that percentage weight loss in the previous 3-6 months was valid to predict nutritional risk.
							</p>
							<p>
								The adjustment for oedema in this calculator is from the BDA Parenteral and Enteral Nutrition Specialist Group: <em>A pocket guide to clinical nutrition</em>. 5th edn, 2018.  Editors V.E. Todorovic and B. Mafrici. This source notes that:  “The evidence supporting these estimates is unknown and clinical experience suggests significant inter-variability, with extreme cases  of oedema adding a weight of over 25kg.” 
							</p>
						`,
					},
					{
						title: "Calculation<span class='show--md'> & interpretation</span>",
						display: false,
						accordion: false,
						content: `
							<p>Points are assigned to the various MUST criteria as indicated below. Total MUST score is calculated by adding the points.</p>
							<table class="table tbl">
								<tr>
									<th width="50%">Criteria</th>
									<th width="25%">Value</th>
									<th width="25%">Points</th>
								</tr>
								<tr>
									<td rowspan="3">BMI (kg/m<sup>2</sup>)</td>
									<td>>20</td>
									<td>0</td>
								</tr>
								<tr>
									<td>18.5-20</td>
									<td>1</td>
								</tr>
								<tr>
									<td><18.5</td>
									<td>2</td>
								</tr>
								<tr>
									<td rowspan="3">Unplanned weight loss in past 3-6 months</td>
									<td><5%</td>
									<td>0</td>
								</tr>
								<tr>
									<td>5-10%</td>
									<td>1</td>
								</tr>
								<tr>
									<td>>10%</td>
									<td>2</td>
								</tr>
								<tr>
									<td rowspan="2">Patient is acutely ill and there has been or is likely to be no nutritional intake for >5 days</td>
									<td>No</td>
									<td>0</td>
								</tr>
								<tr>
									<td>Yes</td>
									<td>2</td>
								</tr>
							</table>
							<p>If any weight loss was planned then unplanned weight loss should be assigned value of 0% and score is 0.</p>
							<p><strong>Adjustment for amputation</strong></p>
							<p>Weight adjustments for amputees are as set out in the <a target="_blank" href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf">BAPEN MUST Explanatory Booklet</a>.  Adjustments apply to Current weight and Heaviest available weight 3-6 months.</p>
							<p>Adjustments of body weight are made from knowledge of missing limb segments: upper limb 4.9% (upper arm 2.7%; forearm, 1.6%; hand, 0.6%); lower limb 15.6% (thigh 9.7%; lower leg 4.5%; foot 1.4%).</p>
							<p><strong>Amputation Calculation</strong></p>
							<table class="table tbl">
								<tr>
									<th width="120">Amputation</th>
									<th>Calculation</th>
								</tr>
								<tr>
									<td>Below knee</td>
									<td>Current weight (kg) x 1.063</td>
								</tr>
								<tr>
									<td>Full leg</td>
									<td>Current weight (kg) x 1.18</td>
								</tr>
								<tr>
									<td>Forearm</td>
									<td>Current weight (kg) x 1.022</td>
								</tr>
								<tr>
									<td>Full arm</td>
									<td>Current weight (kg) x 1.0.53</td>
								</tr>
							</table>
							<p><strong>Adjustment for oedema</strong></p>
							<p>
								Weight adjustments for oedema are set out in the BDA Parenteral and Enteral Nutrition Specialist Group:<em>A pocket guide to clinical nutrition</em>. 5th edn, 2018. Editors V.E. Todorovic and B. Mafrici.
							</p>
							<table class="table tbl">
								<tr>
									<th>Severity of oedema</th>
									<th>Adjustment</th>
								</tr>
								<tr>
									<td>Mild</td>
									<td>Subtract 1.0 kg</td>
								</tr>
								<tr>
									<td>Moderate</td>
									<td>Subtract 5.0 kg</td>
								</tr>
								<tr>
									<td>Severe</td>
									<td>Subtract 10.0 kg</td>
								</tr>
							</table>
							<p><strong>Determining risk of malnutrition</strong></p>
							<table class="table tbl">
								<tr>
									<th>MUST Score</th>
									<th>Risk level</th>
								</tr>
								<tr>
									<td>0</td>
									<td>Low</td>
								</tr>
								<tr>
									<td>1</td>
									<td>Medium</td>
								</tr>
								<tr>
									<td>>=2</td>
									<td>High</td>
								</tr>
							</table>
						`,
					},
					{
						title: "Practice<span class='show--md'> considerations</span>",
						display: false,
						accordion: false,
						content: `
							<p>
								<strong>
									This MUST calculator is designed to aid clinical decision-making. It should be used to <em>support</em> clinical judgement, not as a replacement for it.
								</strong>
							</p>
							<p>The <a href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf" target="_blank">BAPEN MUST explanatory booklet</a> provides detailed practical guidance on how to use the MUST tool, including adjustments and alternative measures for a range of special circumstances.</p>
							<ul>
								<li>If subject cannot be weighed, use a weight recently documented in their notes or use self-reported weight (if reliable and realistic) - from <a href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf" target="_blank">BAPEN MUST explanatory booklet</a></li>
								<li>If height cannot be measured, recently documented or self-reported height may be used, if reliable and realistic.</li>
								<li>If the patient does not know or cannot report their height, height may be estimated by one of the following: ulna length, knee height, demispan.(For further details, see MUST booklet.  )</li>
								<li>If height and weight cannot be obtained, use mid-upper arm circumference (MUAC) in place of BMI. If MUAC is less than 23.5 cm, BMI is likely to be less than 20 kg/m2  i.e. subject is likely to be underweight. If MUAC is more than 32.0 cm, BMI is likely to be more than 30 kg/m2 i.e. subject is likely to be obese. (For further details, see <a href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf" target="_blank">MUST booklet</a>.</li>
								<li>The adjustments for oedema reported in this calculator are from the BDA Parenteral and Enteral Nutrition Specialist Group: A pocket guide to clinical nutrition. 5th edn, 2018. Editors V.E. Todorovic and B. Mafrici. This source notes that : “The evidence supporting the estimates for adjustments for oedema in this calculator is unknown and clinical experience suggests significant inter-variability, with extreme cases  of oedema adding a weight of over 25kg.” </li>
								<li>Training staff to correctly score MUST and to take appropriate next steps in management is an important enabler of universal and routine implementation of this scoring system.</li>
							</ul>
							<p>
								<strong>Potential confounding factors</strong>
							</p>
							<p>
								In interpreting the MUST outputs, clinicians should bear in mind the impact of the following factors.
							</p>
							<ul>
								<li><strong>Constitution:</strong> Some healthy adults are constitutionally thin and have a BMI of less than 20kg/m2. Conversely, it is possible for a very muscular individual (body builder) to have  a BMI at the lower end of the obese range without having excess body fat.</li>
								<li><strong>Fluid disturbances (oedema/dehydration):</strong> Impact of these conditions on weight is difficult to quantify and varies substantially from case to case.  The adjustment made in this online tool, based on the <a href="https://www.bapen.org.uk/screening-and-must/30-screening-for-malnutrition/must" target="_blank">MUST report</a>  and the BDA Pocket guide to nutrition (2018) is a generic estimate and may need to be adapted to the individual and context.</li>
								<li><strong>Muscle wasting</strong> (e.g. due to neurological conditions, prolonged immobility)</li>
								<li><strong>Pregnancy</strong> (see guidance in <a href="https://www.bapen.org.uk/screening-and-must/30-screening-for-malnutrition/must" target="_blank">MUST report</a> )</li>
								<li><strong>Plaster casts</strong> (see guidance in <a href="https://www.bapen.org.uk/screening-and-must/30-screening-for-malnutrition/must" target="_blank">MUST report</a> )</li>
								<li><strong>Rate and direction of weight change</strong> – rapid loss or gain of weight over a 3 month period can have more detrimental effects than the same weight change more gradually over a 6 month period.</li>
							</ul>
							<p><strong>Interpreting scores of 2 or more</strong></p>
							<p>
								In interpreting the cumulative MUST scores, clinicians should be aware that a score 
								of above 2 does not always mean a higher overall risk than a score of 2. For example, 
								a score of 6 resulting from the maximum  “2” for each of the MUST components  due to 
								borderline BMI, unintentional weight loss, acute disease effect, does not necessarily 
								indicate greater risk than for a person with exceptionally low BMI but no unintentional 
								weight loss or acute disease effect, who would only score a total of 2.
							</p>

						`,
					}
				]
			}
		}
	}
</script>